import { useEffect } from "react";

/**
 * Lightweight client-side <head> manager — sets/restores meta tags for
 * static content pages. Avoids pulling react-helmet-async since we only
 * need it on three pages.
 */
export default function useDocumentMeta({
  title,
  description,
  ogTitle,
  ogDescription,
  ogUrl,
  canonical,
}) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta('name="description"'),
      ogTitle: getMeta('property="og:title"'),
      ogDescription: getMeta('property="og:description"'),
      ogUrl: getMeta('property="og:url"'),
      ogType: getMeta('property="og:type"'),
      twTitle: getMeta('name="twitter:title"'),
      twDescription: getMeta('name="twitter:description"'),
      canonical: getCanonical(),
    };
    if (title) document.title = title;
    if (description) setMeta('name="description"', description);
    if (ogTitle) setMeta('property="og:title"', ogTitle);
    if (ogDescription) setMeta('property="og:description"', ogDescription);
    if (ogUrl) setMeta('property="og:url"', ogUrl);
    setMeta('property="og:type"', "website");
    // Twitter/X only falls back to og:* when no name="twitter:*" pair exists.
    // index.html ships one, so it has to be overridden per page.
    if (ogTitle || title) setMeta('name="twitter:title"', ogTitle || title);
    if (ogDescription || description)
      setMeta('name="twitter:description"', ogDescription || description);
    // Every route is served the same shell, so without this each page
    // canonicalises to "/" and gets folded into the homepage.
    if (canonical) setCanonical(canonical);

    return () => {
      // Restore prior values so the rest of the SPA isn't polluted.
      document.title = prev.title || "CarTrack — GPS Tracking";
      restoreMeta('name="description"', prev.description);
      restoreMeta('property="og:title"', prev.ogTitle);
      restoreMeta('property="og:description"', prev.ogDescription);
      restoreMeta('property="og:url"', prev.ogUrl);
      restoreMeta('property="og:type"', prev.ogType);
      restoreMeta('name="twitter:title"', prev.twTitle);
      restoreMeta('name="twitter:description"', prev.twDescription);
      if (prev.canonical) setCanonical(prev.canonical);
    };
  }, [title, description, ogTitle, ogDescription, ogUrl, canonical]);
}

function getCanonical() {
  const el = document.querySelector('link[rel="canonical"]');
  return el ? el.getAttribute("href") : null;
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function getMeta(selector) {
  const el = document.querySelector(`meta[${selector}]`);
  return el ? el.getAttribute("content") : null;
}
function setMeta(selector, content) {
  let el = document.querySelector(`meta[${selector}]`);
  if (!el) {
    el = document.createElement("meta");
    const [k, v] = selector.split("=");
    el.setAttribute(k, v.replace(/"/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function restoreMeta(selector, value) {
  if (value === null || value === undefined) {
    const el = document.querySelector(`meta[${selector}]`);
    if (el) el.parentNode.removeChild(el);
  } else {
    setMeta(selector, value);
  }
}
